import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Plus, 
  MapPin, 
  RefreshCw, 
  Trash2,
  Clock,
  Loader2,
  Pencil,
  Save,
  Key,
  ExternalLink,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const LocationsPage = () => {
  const { isAdmin } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
    api_key: '',
    crm_domain: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Edit location state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    location_id: '',
    api_key: '',
    crm_domain: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/locations`, formData);
      toast.success('Location added successfully');
      setDialogOpen(false);
      setFormData({ name: '', location_id: '', api_key: '', crm_domain: '' });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!locationToDelete) return;
    
    try {
      await axios.delete(`${API}/locations/${locationToDelete.id}`);
      toast.success('Location deleted successfully');
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      fetchLocations();
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const handleSync = async (locationId, type) => {
    setSyncing(prev => ({ ...prev, [`${type}-${locationId}`]: true }));
    try {
      const endpoint = type === 'all' 
        ? `${API}/locations/${locationId}/sync-all`
        : `${API}/locations/${locationId}/sync-${type}`;
      const response = await axios.post(endpoint);
      toast.success(response.data.message);
      if (type === 'all' || type === 'fields' || type === 'calendars') {
        fetchLocations();
      }
    } catch (error) {
      toast.error(`Failed to sync ${type}`);
    } finally {
      setSyncing(prev => ({ ...prev, [`${type}-${locationId}`]: false }));
    }
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setEditFormData({
      name: location.name,
      location_id: location.location_id,
      api_key: '', // Don't show existing key for security
      crm_domain: location.crm_domain || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingLocation) return;
    setSaving(true);
    
    try {
      const updateData = { name: editFormData.name };
      
      // Only include location_id and api_key if they've been changed
      if (editFormData.location_id && editFormData.location_id !== editingLocation.location_id) {
        updateData.location_id = editFormData.location_id;
      }
      if (editFormData.api_key) {
        updateData.api_key = editFormData.api_key;
      }
      if (editFormData.crm_domain && editFormData.crm_domain !== editingLocation.crm_domain) {
        updateData.crm_domain = editFormData.crm_domain;
      }
      
      await axios.put(`${API}/locations/${editingLocation.id}`, updateData);
      toast.success('Location updated successfully');
      setEditDialogOpen(false);
      setEditingLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Locations
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your CRM locations and sync settings
          </p>
        </div>
        {isAdmin() && (
          <Button onClick={() => setDialogOpen(true)} data-testid="add-location-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        )}
      </div>

      {/* Locations Grid */}
      {locations.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No locations added
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Add your first CRM location to start syncing fields and calendars.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {locations.map((location) => (
            <Card key={location.id} className="overflow-hidden" data-testid={`location-card-${location.id}`}>
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-3 border-b">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/locations/${location.id}`}
                        className="block hover:underline"
                        data-testid={`open-location-${location.id}`}
                      >
                        <h3 className="font-semibold text-slate-900 text-sm truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {location.name}
                        </h3>
                      </Link>
                      <p className="text-xs text-slate-500 font-mono truncate">
                        {location.location_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {location.connected ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs px-2 py-0.5">
                        <CheckCircle className="w-3 h-3 mr-0.5" />
                        <span className="hidden sm:inline">Connected</span>
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs px-2 py-0.5">
                        <XCircle className="w-3 h-3 mr-0.5" />
                        <span className="hidden sm:inline">Error</span>
                      </Badge>
                    )}
                  </div>
                </div>
                
                {location.last_sync && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-2">
                    <Clock className="w-3 h-3" />
                    <span>Last sync: {new Date(location.last_sync).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <CardContent className="p-3 space-y-3">
                {/* Sync All Button */}
                <Button 
                  className="w-full h-9 text-sm"
                  onClick={() => handleSync(location.id, 'all')}
                  disabled={syncing[`all-${location.id}`]}
                  data-testid={`sync-all-${location.id}`}
                >
                  {syncing[`all-${location.id}`] ? (
                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1.5" />
                  )}
                  Sync All Data
                </Button>

                {/* Quick Links - Compact */}
                <div className="grid grid-cols-3 gap-2">
                  <Link 
                    to={`/locations/${location.id}/fields`}
                    className="text-center p-2 rounded border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    data-testid={`view-fields-${location.id}`}
                  >
                    <div className="text-base font-bold text-indigo-600">{location.field_count || 0}</div>
                    <div className="text-xs text-slate-500">Fields</div>
                  </Link>
                  <Link 
                    to={`/locations/${location.id}/calendars`}
                    className="text-center p-2 rounded border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    data-testid={`view-calendars-${location.id}`}
                  >
                    <div className="text-base font-bold text-indigo-600">{location.calendar_count || 0}</div>
                    <div className="text-xs text-slate-500">Calendars</div>
                  </Link>
                  <Link 
                    to={`/locations/${location.id}/users`}
                    className="text-center p-2 rounded border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    data-testid={`view-users-${location.id}`}
                  >
                    <div className="text-base font-bold text-indigo-600">{location.user_count || 0}</div>
                    <div className="text-xs text-slate-500">Users</div>
                  </Link>
                </div>

                {/* Sync Request Status - Compact */}
                {location.sync_request_count > 0 && (
                  <div 
                    className="flex items-center justify-between p-2 rounded bg-amber-50 border border-amber-200"
                    data-testid={`sync-requests-${location.id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-amber-600" />
                      <span className="text-xs font-medium text-amber-800">
                        {location.sync_request_count} pending
                      </span>
                    </div>
                    {isAdmin() && (
                      <Link 
                        to="/sync-requests" 
                        className="text-xs text-amber-700 hover:text-amber-900 font-medium"
                      >
                        View →
                      </Link>
                    )}
                  </div>
                )}

                {/* Admin Actions - Compact */}
                {isAdmin() && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="flex-1 text-slate-600 h-8 text-xs"
                      onClick={() => openEditDialog(location)}
                      data-testid={`edit-location-${location.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="flex-1 text-slate-600 h-8 text-xs"
                      onClick={() => openEditDialog(location)}
                      data-testid={`update-keys-${location.id}`}
                    >
                      <Key className="w-3 h-3 mr-1" />
                      Keys
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8"
                      onClick={() => {
                        setLocationToDelete(location);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-location-${location.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Location Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add CRM Location</DialogTitle>
            <DialogDescription>
              Connect a new CRM location by providing the API credentials
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Location Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Main Office"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="location-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="crm_domain">CRM Domain</Label>
                <Input
                  id="crm_domain"
                  placeholder="e.g., crm.optsc.com"
                  value={formData.crm_domain}
                  onChange={(e) => setFormData({ ...formData, crm_domain: e.target.value })}
                  data-testid="crm-domain-input"
                />
                <p className="text-xs text-slate-500">Your agency&apos;s CRM domain (without https://)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location_id">Location ID</Label>
                <Input
                  id="location_id"
                  placeholder="e.g., abc123xyz"
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  required
                  data-testid="location-id-input"
                />
                <p className="text-xs text-slate-500">Found in CRM Settings → Business Profile</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_key">Private Integration Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  placeholder="pit-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  required
                  data-testid="api-key-input"
                />
                <p className="text-xs text-slate-500">Found in Settings → Integrations → Private Integrations</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-location-btn">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Location
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Location</DialogTitle>
            <DialogDescription>
              Update location details and API credentials
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="api">API Keys</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Location Name</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  data-testid="edit-location-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-crm-domain">CRM Domain</Label>
                <Input
                  id="edit-crm-domain"
                  placeholder="e.g., crm.optsc.com"
                  value={editFormData.crm_domain}
                  onChange={(e) => setEditFormData({ ...editFormData, crm_domain: e.target.value })}
                  data-testid="edit-crm-domain"
                />
                <p className="text-xs text-slate-500">Your agency&apos;s CRM domain (without https://)</p>
              </div>
            </TabsContent>
            
            <TabsContent value="api" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-location-id">Location ID</Label>
                <Input
                  id="edit-location-id"
                  placeholder={editingLocation?.location_id}
                  value={editFormData.location_id}
                  onChange={(e) => setEditFormData({ ...editFormData, location_id: e.target.value })}
                  data-testid="edit-location-id"
                />
                <p className="text-xs text-slate-500">Leave empty to keep current value</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-api-key">Private Integration Key</Label>
                <Input
                  id="edit-api-key"
                  type="password"
                  placeholder="Enter new key to update"
                  value={editFormData.api_key}
                  onChange={(e) => setEditFormData({ ...editFormData, api_key: e.target.value })}
                  data-testid="edit-api-key"
                />
                <p className="text-xs text-slate-500">Leave empty to keep current key</p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{locationToDelete?.name}&quot;? This will also remove all synced fields, calendars, and users. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LocationsPage;
